import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import deliveryProfileService from "../../services/deliveryProfileService";

// Async thunks
export const fetchDeliveryProfiles = createAsyncThunk(
  "deliveryProfiles/fetchDeliveryProfiles",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await deliveryProfileService.getDeliveryProfiles(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch delivery profiles"
      );
    }
  }
);

export const fetchDeliveryProfileById = createAsyncThunk(
  "deliveryProfiles/fetchDeliveryProfileById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deliveryProfileService.getDeliveryProfileById(id);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch delivery profile"
      );
    }
  }
);

export const createDeliveryProfile = createAsyncThunk(
  "deliveryProfiles/createDeliveryProfile",
  async ({ profileData, files }, { rejectWithValue }) => {
    try {
      const response = await deliveryProfileService.createDeliveryProfile(
        profileData,
        files
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create delivery profile"
      );
    }
  }
);

export const updateDeliveryProfile = createAsyncThunk(
  "deliveryProfiles/updateDeliveryProfile",
  async ({ id, profileData, files }, { rejectWithValue }) => {
    try {
      const response = await deliveryProfileService.updateDeliveryProfile(
        id,
        profileData,
        files
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update delivery profile"
      );
    }
  }
);

export const deleteDeliveryProfile = createAsyncThunk(
  "deliveryProfiles/deleteDeliveryProfile",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deliveryProfileService.deleteDeliveryProfile(id);
      return { id, response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete delivery profile"
      );
    }
  }
);

export const activateDeliveryProfile = createAsyncThunk(
  "deliveryProfiles/activateDeliveryProfile",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deliveryProfileService.activateDeliveryProfile(id);
      return { id, response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to activate delivery profile"
      );
    }
  }
);

export const deactivateDeliveryProfile = createAsyncThunk(
  "deliveryProfiles/deactivateDeliveryProfile",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deliveryProfileService.deactivateDeliveryProfile(
        id
      );
      return { id, response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to deactivate delivery profile"
      );
    }
  }
);

export const approveDeliveryProfile = createAsyncThunk(
  "deliveryProfiles/approveDeliveryProfile",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deliveryProfileService.approveDeliveryProfile(id);
      return { id, response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to approve delivery profile"
      );
    }
  }
);

export const fetchDeliveryProfileStats = createAsyncThunk(
  "deliveryProfiles/fetchDeliveryProfileStats",
  async (_, { rejectWithValue }) => {
    try {
      const response = await deliveryProfileService.getDeliveryProfileStats();
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch delivery profile stats"
      );
    }
  }
);

export const fetchDeliveryHistory = createAsyncThunk(
  "deliveryProfiles/fetchDeliveryHistory",
  async ({ id, params = {} }, { rejectWithValue }) => {
    try {
      const response = await deliveryProfileService.getDeliveryHistory(
        id,
        params
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch delivery history"
      );
    }
  }
);

// Initial state
const initialState = {
  deliveryProfiles: [],
  currentDeliveryProfile: null,
  deliveryHistory: [],
  stats: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isLoadingHistory: false,
  error: null,
  success: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
  historyPagination: {
    page: 1,
    limit: 20,
    total: 0,
  },
};

// Slice
const deliveryProfileSlice = createSlice({
  name: "deliveryProfiles",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    clearCurrentDeliveryProfile: (state) => {
      state.currentDeliveryProfile = null;
    },
    setCurrentDeliveryProfile: (state, action) => {
      state.currentDeliveryProfile = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch delivery profiles
      .addCase(fetchDeliveryProfiles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDeliveryProfiles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.deliveryProfiles = action.payload.data || [];
        state.pagination = action.payload.pagination || state.pagination;
        state.stats = action.payload.stats || null;
      })
      .addCase(fetchDeliveryProfiles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch delivery profile by ID
      .addCase(fetchDeliveryProfileById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDeliveryProfileById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentDeliveryProfile = action.payload.data;
      })
      .addCase(fetchDeliveryProfileById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create delivery profile
      .addCase(createDeliveryProfile.pending, (state) => {
        state.isCreating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(createDeliveryProfile.fulfilled, (state, action) => {
        state.isCreating = false;
        state.success =
          action.payload.message || "Delivery profile created successfully";
        state.deliveryProfiles.unshift(action.payload.data);
      })
      .addCase(createDeliveryProfile.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload;
      })

      // Update delivery profile
      .addCase(updateDeliveryProfile.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateDeliveryProfile.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.success =
          action.payload.message || "Delivery profile updated successfully";
        const index = state.deliveryProfiles.findIndex(
          (profile) => profile.id === action.payload.data.id
        );
        if (index !== -1) {
          state.deliveryProfiles[index] = action.payload.data;
        }
        if (state.currentDeliveryProfile?.id === action.payload.data.id) {
          state.currentDeliveryProfile = action.payload.data;
        }
      })
      .addCase(updateDeliveryProfile.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      // Delete delivery profile
      .addCase(deleteDeliveryProfile.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deleteDeliveryProfile.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.success =
          action.payload.response.message ||
          "Delivery profile deleted successfully";
        state.deliveryProfiles = state.deliveryProfiles.filter(
          (profile) => profile.id !== action.payload.id
        );
        if (state.currentDeliveryProfile?.id === action.payload.id) {
          state.currentDeliveryProfile = null;
        }
      })
      .addCase(deleteDeliveryProfile.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload;
      })

      // Activate delivery profile
      .addCase(activateDeliveryProfile.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(activateDeliveryProfile.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.success =
          action.payload.response.message ||
          "Delivery profile activated successfully";
        const index = state.deliveryProfiles.findIndex(
          (profile) => profile.id === action.payload.id
        );
        if (index !== -1) {
          state.deliveryProfiles[index].is_active = true;
        }
        if (state.currentDeliveryProfile?.id === action.payload.id) {
          state.currentDeliveryProfile.is_active = true;
        }
      })
      .addCase(activateDeliveryProfile.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      // Deactivate delivery profile
      .addCase(deactivateDeliveryProfile.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(deactivateDeliveryProfile.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.success =
          action.payload.response.message ||
          "Delivery profile deactivated successfully";
        const index = state.deliveryProfiles.findIndex(
          (profile) => profile.id === action.payload.id
        );
        if (index !== -1) {
          state.deliveryProfiles[index].is_active = false;
        }
        if (state.currentDeliveryProfile?.id === action.payload.id) {
          state.currentDeliveryProfile.is_active = false;
        }
      })
      .addCase(deactivateDeliveryProfile.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      // Approve delivery profile
      .addCase(approveDeliveryProfile.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
        state.success = null;
      })
      .addCase(approveDeliveryProfile.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.success =
          action.payload.response.message ||
          "Delivery profile approved successfully";
        const index = state.deliveryProfiles.findIndex(
          (profile) => profile.id === action.payload.id
        );
        if (index !== -1) {
          // Update is_verified from the response data
          state.deliveryProfiles[index].is_verified =
            action.payload.response.data?.is_verified ?? true;
        }
        if (state.currentDeliveryProfile?.id === action.payload.id) {
          state.currentDeliveryProfile.is_verified =
            action.payload.response.data?.is_verified ?? true;
        }
      })
      .addCase(approveDeliveryProfile.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload;
      })

      // Fetch delivery profile stats
      .addCase(fetchDeliveryProfileStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDeliveryProfileStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload.data;
      })
      .addCase(fetchDeliveryProfileStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch delivery history
      .addCase(fetchDeliveryHistory.pending, (state) => {
        state.isLoadingHistory = true;
        state.error = null;
      })
      .addCase(fetchDeliveryHistory.fulfilled, (state, action) => {
        state.isLoadingHistory = false;
        state.deliveryHistory = action.payload.data || [];
        state.historyPagination =
          action.payload.pagination || state.historyPagination;
      })
      .addCase(fetchDeliveryHistory.rejected, (state, action) => {
        state.isLoadingHistory = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  clearSuccess,
  clearCurrentDeliveryProfile,
  setCurrentDeliveryProfile,
} = deliveryProfileSlice.actions;

export default deliveryProfileSlice.reducer;
